import { ArrowRight, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Eyebrow } from "@/components/eyebrow";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import type { AnswerSuggestion } from "@/lib/api";
import { cn } from "@/lib/utils";

import type { WizardScreen } from "./types";

// A chosen answer stays highlighted briefly before the wizard auto-advances.
const HOLD_MS = 200;
// Crossfade window: outgoing and incoming screens overlap for this long. Must be
// >= the longest .q-* animation in globals.css (enter = 120ms delay + 420ms).
const ANIM_MS = 480;

interface QuestionWizardProps {
  screens: WizardScreen[];
  totalQuestions: number;
  answers: Record<string, number>;
  suggestions: Record<string, AnswerSuggestion>;
  startQuestionId?: string;
  onAnswer: (questionId: string, value: number) => void;
  onSkip: (questionId: string) => void;
  /** Reports the current question id so a restored draft can resume in place. */
  onQuestionChange?: (questionId: string) => void;
  onBack: () => void;
  onComplete: () => void;
}

export function QuestionWizard({
  screens,
  totalQuestions,
  answers,
  suggestions,
  startQuestionId,
  onAnswer,
  onSkip,
  onQuestionChange,
  onBack,
  onComplete,
}: QuestionWizardProps) {
  const initialIndex = startQuestionId
    ? Math.max(
        0,
        screens.findIndex(
          (s) => s.type === "question" && s.question.question_id === startQuestionId,
        ),
      )
    : 0;

  const [index, setIndex] = useState(initialIndex);
  const [showWhy, setShowWhy] = useState(false);
  // The screen leaving, rendered stacked under the incoming one during the fade.
  const [outgoing, setOutgoing] = useState<number | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Locked while a crossfade is in flight so holding Enter can't skip screens
  // with no visual continuity; each advance waits for the fade to settle.
  const lockRef = useRef(false);
  // A move requested mid-crossfade is queued here (last-wins, depth 1) and run
  // when the lock releases, so an auto-advance that fires during the fade isn't
  // silently dropped. Drained via goRef to dodge the timer's stale `go` closure.
  const pendingRef = useRef<number | null>(null);

  const screen = screens[index];

  const go = useCallback(
    (delta: number) => {
      if (lockRef.current) {
        // Mid-transition: remember the latest request and run it on release.
        pendingRef.current = delta;
        return;
      }
      // Cancel a pending auto-advance so a manual click can't double-step.
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
      const next = index + delta;
      if (next < 0) {
        onBack();
        return;
      }
      if (next >= screens.length) {
        onComplete();
        return;
      }
      // Reduced motion disables the fade (globals.css), so the transition lock
      // would add 480ms of input latency with nothing on screen to justify it.
      // Swap instantly instead, and drop any stale outgoing cell.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setShowWhy(false);
        setOutgoing(null);
        setIndex(next);
        return;
      }
      lockRef.current = true;
      setShowWhy(false);
      setOutgoing(index);
      setIndex(next);
      if (animTimer.current) {
        clearTimeout(animTimer.current);
      }
      animTimer.current = setTimeout(() => {
        animTimer.current = null;
        lockRef.current = false;
        const pending = pendingRef.current;
        if (pending !== null) {
          // Run the queued move: a fresh crossfade off the now-current screen.
          pendingRef.current = null;
          goRef.current(pending);
        } else {
          setOutgoing(null);
        }
      }, ANIM_MS);
    },
    [index, screens.length, onBack, onComplete],
  );

  // Keep a live handle on the latest `go` so the animTimer callback (created in a
  // prior render) drains the queue against current state, not a stale closure.
  const goRef = useRef(go);
  useEffect(() => {
    goRef.current = go;
  }, [go]);

  const pick = useCallback(
    (questionId: string, value: number) => {
      onAnswer(questionId, value);
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
      }
      holdTimer.current = setTimeout(() => go(1), HOLD_MS);
    },
    [onAnswer, go],
  );

  const clear = useCallback(
    (questionId: string) => {
      // Cancel a pending auto-advance so clearing never advances the wizard.
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
        holdTimer.current = null;
      }
      onSkip(questionId);
    },
    [onSkip],
  );

  // Keyboard shortcuts: digits pick an option, ArrowLeft goes back,
  // Enter/ArrowRight advances (also begins a section on intro screens).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (!screen) {
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        go(-1);
        return;
      }
      if (screen.type === "intro") {
        if (e.key === "Enter" || e.key === "ArrowRight") {
          e.preventDefault();
          go(1);
        }
        return;
      }
      const n = Number(e.key);
      const opt = Number.isInteger(n) && n >= 1 ? screen.question.options[n - 1] : undefined;
      if (opt) {
        e.preventDefault();
        pick(screen.question.question_id, opt.value);
        return;
      }
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        go(1);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [screen, go, pick]);

  useEffect(() => {
    return () => {
      if (holdTimer.current) {
        clearTimeout(holdTimer.current);
      }
      if (animTimer.current) {
        clearTimeout(animTimer.current);
      }
    };
  }, []);

  // Surface the current question as the resume point whenever it changes.
  // Intro screens carry no question id, so they leave the last point in place.
  useEffect(() => {
    if (screen?.type === "question") {
      onQuestionChange?.(screen.question.question_id);
    }
  }, [screen, onQuestionChange]);

  if (!screen) {
    return null;
  }

  const questionsPassed = screens.slice(0, index).filter((s) => s.type === "question").length;
  const progressPct = Math.round((questionsPassed / totalQuestions) * 100);

  const renderScreen = (i: number) => {
    const s = screens[i];
    if (!s) {
      return null;
    }
    if (s.type === "intro") {
      return <IntroBody screen={s} onNavigate={go} />;
    }
    return (
      <QuestionBody
        screen={s}
        totalQuestions={totalQuestions}
        answers={answers}
        suggestions={suggestions}
        showWhy={showWhy}
        onToggleWhy={() => setShowWhy((v) => !v)}
        onPick={pick}
        onSkip={onSkip}
        onClear={clear}
        onNavigate={go}
      />
    );
  };

  return (
    <div>
      <Progress
        value={progressPct}
        className="[&>[data-slot=progress-indicator]]:bg-accent mb-8 h-1"
      />
      {/* Outgoing and incoming screens share one grid cell so they overlap and
          cross-fade; min-height keeps the footer from jumping between screens. */}
      <div className="grid min-h-[380px]">
        {outgoing !== null && (
          <div
            key={`out-${outgoing}`}
            className="q-leave pointer-events-none col-start-1 row-start-1"
            aria-hidden
            inert
          >
            {renderScreen(outgoing)}
          </div>
        )}
        <div key={index} className="q-enter col-start-1 row-start-1">
          {renderScreen(index)}
        </div>
      </div>
    </div>
  );
}

interface IntroBodyProps {
  screen: Extract<WizardScreen, { type: "intro" }>;
  onNavigate: (delta: number) => void;
}

function IntroBody({ screen, onNavigate }: IntroBodyProps) {
  const stepNo =
    screen.layer === "business_model" ? "2" : screen.layer === "operating_model" ? "3" : "4";
  return (
    <div>
      <Eyebrow>Section {stepNo} of 4</Eyebrow>
      <h1 className="text-primary mt-3 max-w-[16ch] text-4xl font-normal tracking-tight sm:text-5xl">
        {screen.title}.
      </h1>
      <p className="text-muted-foreground mt-4 text-lg">
        {screen.questionCount} questions across {screen.dimensionCount} dimensions. Pick the option
        that fits best. There are no wrong answers, and you can skip anything you’re unsure about.
      </p>
      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={() => onNavigate(-1)}>
          ← Back
        </Button>
        <Button onClick={() => onNavigate(1)}>
          Begin section <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface QuestionBodyProps {
  screen: Extract<WizardScreen, { type: "question" }>;
  totalQuestions: number;
  answers: Record<string, number>;
  suggestions: Record<string, AnswerSuggestion>;
  showWhy: boolean;
  onToggleWhy: () => void;
  onPick: (questionId: string, value: number) => void;
  onSkip: (questionId: string) => void;
  onClear: (questionId: string) => void;
  onNavigate: (delta: number) => void;
}

function QuestionBody({
  screen,
  totalQuestions,
  answers,
  suggestions,
  showWhy,
  onToggleWhy,
  onPick,
  onSkip,
  onClear,
  onNavigate,
}: QuestionBodyProps) {
  const qid = screen.question.question_id;
  const selected = answers[qid];
  const answered = selected !== undefined;
  const suggestion = suggestions[qid];
  const isSuggested = suggestion !== undefined && selected === suggestion.value;

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <Eyebrow>
          {screen.layerTitle} · {screen.dimensionName}
        </Eyebrow>
        <span className="text-muted shrink-0 font-mono text-xs">
          {screen.questionNumber} of {totalQuestions}
        </span>
      </div>

      <p className="text-primary mt-3 text-2xl font-normal tracking-tight sm:text-3xl">
        {screen.question.text}
      </p>
      <p className="text-muted-foreground mt-2 text-sm">{screen.question.guidance}</p>

      <button
        type="button"
        className="text-accent mt-3 mb-4 inline-flex items-center gap-1 text-xs font-medium hover:underline"
        onClick={onToggleWhy}
      >
        {showWhy ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        Why we ask
      </button>
      {showWhy && (
        <p className="border-accent text-muted-foreground mb-4 border-l-2 pl-4 text-sm">
          {screen.description}
        </p>
      )}

      {suggestion && (
        <div className="mb-4">
          <span className="bg-accent-light/60 text-accent inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase">
            <Sparkles className="h-3.5 w-3.5" /> Suggested from your documents
          </span>
          <p className="text-muted mt-2 text-xs">{suggestion.rationale}</p>
        </div>
      )}

      <div className="space-y-2">
        {screen.question.options.map((opt, i) => (
          <button
            type="button"
            key={opt.label}
            className={cn(
              "border-border bg-card hover:border-accent flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm transition-colors",
              selected === opt.value && "border-accent bg-accent-light/25",
            )}
            onClick={() => onPick(qid, opt.value)}
          >
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded border font-mono text-xs",
                selected === opt.value
                  ? "border-accent bg-accent text-white"
                  : "border-border bg-background text-muted",
              )}
            >
              {i + 1}
            </span>
            <span className="text-foreground">{opt.label}</span>
            {isSuggested && suggestion.value === opt.value && (
              <span className="text-accent ml-auto font-mono text-[11px] tracking-wide uppercase">
                suggested
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button variant="ghost" onClick={() => onNavigate(-1)}>
          ← Back
        </Button>
        {answered ? (
          <div className="flex items-center gap-3">
            {/* Clear without advancing: the footer flips back to the skip
                state, showing the question is now unanswered (excluded from
                scoring). */}
            <button
              type="button"
              className="text-muted hover:text-accent text-xs font-medium hover:underline"
              onClick={() => onClear(qid)}
            >
              Clear answer
            </button>
            <Button onClick={() => onNavigate(1)}>
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => {
              // No answer to keep, clear any stale value and move on.
              onSkip(qid);
              onNavigate(1);
            }}
          >
            Not sure, skip <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
